import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Clock, Users, Award, Phone, Mail, MessageCircle, CheckCircle, BookOpen, Shield, MapPin } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const Index = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { type: 'bot', message: '¡Hola! Soy tu asistente virtual. ¿En qué puedo ayudarte con el entrenamiento en Seguridad y Salud en el Trabajo?' }
  ]);
  const [currentMessage, setCurrentMessage] = useState('');

  const cronograma = [
    {
      fecha: '5 dic. 2025',
      horario: '9:00 – 12:30',
      tema: 'Identificación de riesgos',
      actividad: 'Apertura, normativa básica, tipología de riesgos, taller práctico de identificación',
      material: 'Matriz de identificación y evaluación de riesgos laborales (versión gratuita)'
    },
    {
      fecha: '6 dic. 2025',
      horario: '9:00 – 12:30',
      tema: 'Evaluación de riesgos',
      actividad: 'Métodos cualitativos y cuantitativos, uso de matrices, ejercicio aplicado',
      material: 'Ejemplo práctico de matriz aplicada'
    },
    {
      fecha: '7 dic. 2025',
      horario: '9:00 – 12:30',
      tema: 'Prevención y control de riesgos',
      actividad: 'Principios de prevención, planes de seguridad, simulación práctica, clausura',
      material: 'Certificado de participación'
    }
  ];

  const contactos = [
    { nombre: 'Eliana', email: 'eliana@disaic.cu', whatsapp: '+53 59982269' },
    { nombre: 'Suharmy', email: 'suharmy@disaic.cu', whatsapp: '+53 52160522' },
    { nombre: 'Alexander', email: 'alexander@disaic.cu', whatsapp: '+53 52120442' }
  ];

  const handleSendMessage = () => {
    if (currentMessage.trim()) {
      setChatMessages([...chatMessages, { type: 'user', message: currentMessage }]);
      
      // Simulación de respuesta del bot
      setTimeout(() => {
        let botResponse = '';
        const msg = currentMessage.toLowerCase();
        
        if (msg.includes('precio') || msg.includes('costo')) {
          botResponse = 'El precio del entrenamiento es de 2000.00 CUP por participante. Se requiere un mínimo de 15 participantes.';
        } else if (msg.includes('fecha') || msg.includes('cuando')) {
          botResponse = 'El entrenamiento se realizará del 5 al 7 de diciembre de 2025, de 9:00 a.m. a 12:30 p.m.';
        } else if (msg.includes('ubicacion') || msg.includes('donde') || msg.includes('sede') || msg.includes('direccion')) {
          botResponse = 'El entrenamiento se realizará en nuestra sede: Casa Consultora DISAIC, Calle Buenos Aires No. 100, esq. a Leonor, Cerro, La Habana, Cuba.';
        } else if (msg.includes('matricula') || msg.includes('inscrib')) {
          botResponse = 'Para matricularte, puedes contactar a nuestro equipo comercial por WhatsApp o correo. ¡Te ayudo a conectarte!';
        } else if (msg.includes('certificado')) {
          botResponse = 'Sí, al finalizar el entrenamiento recibirás un certificado de participación oficial.';
        } else {
          botResponse = 'Gracias por tu consulta. Para información más específica, te recomiendo contactar directamente con nuestro equipo comercial.';
        }
        
        setChatMessages(prev => [...prev, { type: 'bot', message: botResponse }]);
      }, 1000);
      
      setCurrentMessage('');
    }
  };

  const openWhatsApp = (number: string) => {
    const cleanNumber = number.replace(/[^0-9]/g, '');
    const message = encodeURIComponent('Hola, me interesa el entrenamiento en Seguridad y Salud en el Trabajo del 5-7 de diciembre 2025.');
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${message}`;
    
    // Intentar abrir WhatsApp, si falla abrir en nueva pestaña
    try {
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.log('Abriendo WhatsApp:', whatsappUrl);
      window.location.href = whatsappUrl;
    }
  };

  const openEmail = (email: string) => {
    const subject = encodeURIComponent('Consulta sobre Entrenamiento en Seguridad y Salud en el Trabajo');
    const body = encodeURIComponent('Hola,\n\nMe interesa obtener más información sobre el entrenamiento en Seguridad y Salud en el Trabajo que se realizará del 5 al 7 de diciembre de 2025.\n\nEspero su respuesta.\n\nSaludos.');
    const emailUrl = `mailto:${email}?subject=${subject}&body=${body}`;
    
    // Intentar abrir cliente de correo
    try {
      window.open(emailUrl, '_blank');
    } catch (error) {
      console.log('Abriendo correo:', emailUrl);
      window.location.href = emailUrl;
    }
  };

  const handleMatricula = (formData: FormData) => {
    const nombre = formData.get('nombre') as string;
    const telefono = formData.get('telefono') as string;
    const email = formData.get('email') as string;
    const empresa = formData.get('empresa') as string;
    const mensaje = formData.get('mensaje') as string;
    
    const subject = encodeURIComponent('Solicitud de Matrícula - Entrenamiento SST');
    const body = encodeURIComponent(
      `Solicitud de Matrícula\n\n` +
      `Nombre: ${nombre}\n` +
      `Teléfono: ${telefono}\n` +
      `Email: ${email}\n` +
      `Empresa: ${empresa}\n` +
      `Mensaje: ${mensaje}\n\n` +
      `Entrenamiento: Seguridad y Salud en el Trabajo\n` +
      `Fechas: 5-7 Diciembre 2025\n` +
      `Horario: 9:00 AM - 12:30 PM\n` +
      `Precio: 2000 CUP por participante`
    );
    
    // Enviar a Eliana (primer contacto)
    const emailUrl = `mailto:eliana@disaic.cu?subject=${subject}&body=${body}`;
    window.open(emailUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-primary">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-primary">Casa Consultora DISAIC</h1>
              <p className="text-muted-foreground">Especialistas en Seguridad y Salud en el Trabajo</p>
            </div>
            <Shield className="h-12 w-12 text-primary" />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-accent text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              ENTRENAMIENTO EN SEGURIDAD Y SALUD EN EL TRABAJO
            </h1>
            <p className="text-xl mb-8 opacity-90">
              Capacítate con los mejores especialistas y obtén las herramientas necesarias para gestionar riesgos laborales de manera efectiva
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Badge variant="secondary" className="text-lg px-4 py-2">
                <Calendar className="w-5 h-5 mr-2" />
                5-7 Diciembre 2025
              </Badge>
              <Badge variant="secondary" className="text-lg px-4 py-2">
                <Clock className="w-5 h-5 mr-2" />
                9:00 AM - 12:30 PM
              </Badge>
              <Badge variant="secondary" className="text-lg px-4 py-2">
                <Users className="w-5 h-5 mr-2" />
                2000 CUP
              </Badge>
              <Badge variant="secondary" className="text-lg px-4 py-2">
                <MapPin className="w-5 h-5 mr-2" />
                La Habana, Cuba
              </Badge>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="lg" variant="secondary" className="text-lg px-8 py-4 hover:scale-105 transition-transform">
                    <Award className="w-5 h-5 mr-2" />
                    ¡Matricúlate Ahora!
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Opciones de Matrícula</DialogTitle>
                    <DialogDescription>
                      Elige cómo prefieres contactarnos para matricularte
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid gap-3">
                      {contactos.map((contacto, index) => (
                        <div key={index} className="flex gap-2">
                          <Button 
                            onClick={() => openWhatsApp(contacto.whatsapp)}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            <MessageCircle className="w-4 h-4 mr-2" />
                            WhatsApp {contacto.nombre}
                          </Button>
                          <Button 
                            onClick={() => openEmail(contacto.email)}
                            variant="outline" 
                            className="flex-1"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Email {contacto.nombre}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <Tabs defaultValue="cronograma" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="cronograma">Cronograma</TabsTrigger>
            <TabsTrigger value="beneficios">Beneficios</TabsTrigger>
            <TabsTrigger value="contacto">Contacto</TabsTrigger>
            <TabsTrigger value="matricula">Matrícula</TabsTrigger>
          </TabsList>

          <TabsContent value="cronograma">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-center text-primary mb-8">Cronograma Temático</h2>
              <div className="grid gap-6">
                {cronograma.map((dia, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow border-l-4 border-l-primary">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl text-primary">{dia.tema}</CardTitle>
                          <CardDescription className="text-lg">
                            <Calendar className="w-4 h-4 inline mr-2" />
                            {dia.fecha} | {dia.horario}
                          </CardDescription>
                        </div>
                        <Badge variant="outline" className="text-primary border-primary">
                          Día {index + 1}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-primary mb-2">Actividades:</h4>
                          <p className="text-muted-foreground">{dia.actividad}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-primary mb-2">Material entregado:</h4>
                          <p className="text-muted-foreground flex items-center">
                            <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                            {dia.material}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="beneficios">
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-center text-primary mb-8">Valor Añadido</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center text-primary">
                      <BookOpen className="w-6 h-6 mr-3" />
                      Matriz Gratuita
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Los participantes recibirán una matriz gratuita que servirá como herramienta práctica para aplicar lo aprendido en la identificación y evaluación de riesgos.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center text-primary">
                      <Shield className="w-6 h-6 mr-3" />
                      Compendio de Normas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Los participantes recibirán como cortesía un compendio de Normas Técnicas asociadas a la correcta Gestión de Riesgos.
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <Card className="bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
                <CardHeader>
                  <CardTitle className="text-center text-primary text-2xl">
                    <Award className="w-8 h-8 mx-auto mb-2" />
                    Certificado de Participación
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-center text-muted-foreground text-lg">
                    Al completar el entrenamiento, recibirás un certificado oficial que avala tu capacitación en Seguridad y Salud en el Trabajo.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="contacto">
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-center text-primary mb-8">Equipo Comercial</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {contactos.map((contacto, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow text-center">
                    <CardHeader>
                      <CardTitle className="text-primary">{contacto.nombre}</CardTitle>
                      <CardDescription>Asesor Comercial</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Button 
                        onClick={() => openWhatsApp(contacto.whatsapp)}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        WhatsApp: {contacto.whatsapp}
                      </Button>
                      <Button 
                        onClick={() => openEmail(contacto.email)}
                        variant="outline" 
                        className="w-full"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        {contacto.email}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Sección de Ubicación */}
              <div className="mt-12">
                <h3 className="text-2xl font-bold text-center text-primary mb-6">Ubicación de la Sede</h3>
                <Card className="max-w-2xl mx-auto">
                  <CardHeader>
                    <CardTitle className="flex items-center text-primary">
                      <MapPin className="w-6 h-6 mr-3" />
                      Casa Consultora DISAIC
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <p className="text-lg font-medium">Dirección:</p>
                      <p className="text-muted-foreground">
                        Calle Buenos Aires No. 100, esq. a Leonor<br />
                        Cerro, La Habana, Cuba
                      </p>
                      <div className="pt-4">
                        <Button 
                          onClick={() => window.open('https://maps.google.com/?q=Calle+Buenos+Aires+100+Cerro+La+Habana+Cuba', '_blank')}
                          className="w-full"
                        >
                          <MapPin className="w-4 h-4 mr-2" />
                          Ver en Google Maps
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="matricula">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-center text-primary mb-8">Formulario de Matrícula</h2>
              <Card>
                <CardHeader>
                  <CardTitle>Información del Participante</CardTitle>
                  <CardDescription>
                    Completa tus datos para proceder con la matrícula
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form action={(formData) => handleMatricula(formData)} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="nombre">Nombre Completo</Label>
                        <Input id="nombre" name="nombre" placeholder="Tu nombre completo" required />
                      </div>
                      <div>
                        <Label htmlFor="telefono">Teléfono</Label>
                        <Input id="telefono" name="telefono" placeholder="Tu número de teléfono" required />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Correo Electrónico</Label>
                      <Input id="email" name="email" type="email" placeholder="tu@email.com" required />
                    </div>
                    <div>
                      <Label htmlFor="empresa">Empresa/Organización</Label>
                      <Input id="empresa" name="empresa" placeholder="Nombre de tu empresa" />
                    </div>
                    <div>
                      <Label htmlFor="mensaje">Mensaje Adicional</Label>
                      <Textarea id="mensaje" name="mensaje" placeholder="Cualquier consulta adicional..." />
                    </div>
                    <Button type="submit" className="w-full" size="lg">
                      Enviar Solicitud de Matrícula
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Chat AI Button */}
      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogTrigger asChild>
          <Button 
            className="fixed bottom-6 right-6 rounded-full w-16 h-16 shadow-lg hover:scale-110 transition-transform"
            size="lg"
          >
            <MessageCircle className="w-6 h-6" />
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md max-h-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <MessageCircle className="w-5 h-5 mr-2" />
              Asistente Virtual SST
            </DialogTitle>
            <DialogDescription>
              Pregúntame sobre el entrenamiento en Seguridad y Salud en el Trabajo
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="h-80 overflow-y-auto space-y-3 p-4 bg-muted/30 rounded-lg">
              {chatMessages.map((msg, index) => (
                <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] p-3 rounded-lg ${
                    msg.type === 'user' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-white border'
                  }`}>
                    <p className="text-sm">{msg.message}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input 
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                placeholder="Escribe tu pregunta..."
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <Button onClick={handleSendMessage}>
                Enviar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-12">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold mb-4">Casa Consultora DISAIC s.u.r.l</h3>
          <p className="text-lg opacity-90 mb-6">
            Especialistas en Seguridad y Salud en el Trabajo
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => openWhatsApp('+53 59982269')}
              variant="secondary" 
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp
            </Button>
            <Button 
              onClick={() => openEmail('eliana@disaic.cu')}
              variant="outline" 
              size="lg" 
              className="text-white border-white hover:bg-white hover:text-primary"
            >
              <Mail className="w-5 h-5 mr-2" />
              Email
            </Button>
            <Button 
              onClick={() => window.open('tel:+5359982269', '_self')}
              variant="secondary" 
              size="lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              Llamar
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
